
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigResponse} from './ConfigResponse';
import {FieldModelConfigResponse} from './ConfigResponse';
import {Timezone} from '../core/Timezone';
import {FieldModelTimezone} from '../core/Timezone';

/**
 * ManagerPlatformTimezoneGetResponse struct
 */
class ManagerPlatformTimezoneGetResponse extends ConfigResponse {
  /**
   * Initialize struct
   * @param {!ConfigResponse=} parent
   * @param {!Timezone=} timezone
   * @constructor
   */
  constructor (parent = new ConfigResponse(), timezone = new Timezone()) {
    super()
    super.copy(parent)
    this.Timezone = timezone
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ManagerPlatformTimezoneGetResponse}
   * @param {!ManagerPlatformTimezoneGetResponse} other Other struct
   * @returns {!ManagerPlatformTimezoneGetResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.Timezone != null) {
      this.Timezone = Timezone.fromObject(other.Timezone)
    } else {
      this.Timezone = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ManagerPlatformTimezoneGetResponse}
   * @returns {!ManagerPlatformTimezoneGetResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ManagerPlatformTimezoneGetResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ManagerPlatformTimezoneGetResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ManagerPlatformTimezoneGetResponse}
   * @param {!ManagerPlatformTimezoneGetResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ManagerPlatformTimezoneGetResponse)) {
      throw new TypeError('Instance of ManagerPlatformTimezoneGetResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ManagerPlatformTimezoneGetResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      Timezone: ((this.Timezone != null) ? this.Timezone : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ManagerPlatformTimezoneGetResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ManagerPlatformTimezoneGetResponse} other Object value
   * @returns {!ManagerPlatformTimezoneGetResponse} Created struct
   */
  static fromObject (other) {
    return new ManagerPlatformTimezoneGetResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ManagerPlatformTimezoneGetResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ManagerPlatformTimezoneGetResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ManagerPlatformTimezoneGetResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6463
  }
}

export { ManagerPlatformTimezoneGetResponse };

/**
 * Fast Binary Encoding ManagerPlatformTimezoneGetResponse field model
 */
class FieldModelManagerPlatformTimezoneGetResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigResponse(buffer, 4 + 4)
    this.Timezone = new FieldModelTimezone(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
  }

  /**
   * Get the ConfigResponse field model
   * @this {!FieldModelManagerPlatformTimezoneGetResponse}
   * @returns {!FieldModelConfigResponse} ConfigResponse field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelManagerPlatformTimezoneGetResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelManagerPlatformTimezoneGetResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.Timezone.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelManagerPlatformTimezoneGetResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.Timezone.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelManagerPlatformTimezoneGetResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelManagerPlatformTimezoneGetResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelManagerPlatformTimezoneGetResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6463
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelManagerPlatformTimezoneGetResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelManagerPlatformTimezoneGetResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Timezone.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Timezone.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Timezone.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelManagerPlatformTimezoneGetResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelManagerPlatformTimezoneGetResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelManagerPlatformTimezoneGetResponse}
   * @param {!ManagerPlatformTimezoneGetResponse} fbeValue Default value, defaults is new ManagerPlatformTimezoneGetResponse()
   * @returns {!ManagerPlatformTimezoneGetResponse} ManagerPlatformTimezoneGetResponse value
   */
  get (fbeValue = new ManagerPlatformTimezoneGetResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelManagerPlatformTimezoneGetResponse}
   * @param {!ManagerPlatformTimezoneGetResponse} fbeValue ManagerPlatformTimezoneGetResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Timezone.fbeSize) <= fbeStructSize) {
      fbeValue.Timezone = this.Timezone.get()
    } else {
      fbeValue.Timezone = new Timezone()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Timezone.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelManagerPlatformTimezoneGetResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelManagerPlatformTimezoneGetResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelManagerPlatformTimezoneGetResponse}
   * @param {!ManagerPlatformTimezoneGetResponse} fbeValue ManagerPlatformTimezoneGetResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelManagerPlatformTimezoneGetResponse}
   * @param {ManagerPlatformTimezoneGetResponse} fbeValue ManagerPlatformTimezoneGetResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.Timezone.set(fbeValue.Timezone)
  }
}

export { FieldModelManagerPlatformTimezoneGetResponse };

/**
 * Fast Binary Encoding ManagerPlatformTimezoneGetResponse model
 */
class ManagerPlatformTimezoneGetResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelManagerPlatformTimezoneGetResponse(this.buffer, 4)
  }

  /**
   * Get the ManagerPlatformTimezoneGetResponse model
   * @this {!ManagerPlatformTimezoneGetResponseModel}
   * @returns {!FieldModelManagerPlatformTimezoneGetResponse} model ManagerPlatformTimezoneGetResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ManagerPlatformTimezoneGetResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ManagerPlatformTimezoneGetResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ManagerPlatformTimezoneGetResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ManagerPlatformTimezoneGetResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelManagerPlatformTimezoneGetResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ManagerPlatformTimezoneGetResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ManagerPlatformTimezoneGetResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ManagerPlatformTimezoneGetResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ManagerPlatformTimezoneGetResponseModel}
   * @param {!ManagerPlatformTimezoneGetResponse} value ManagerPlatformTimezoneGetResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ManagerPlatformTimezoneGetResponseModel}
   * @param {!ManagerPlatformTimezoneGetResponse} value ManagerPlatformTimezoneGetResponse value, defaults is new ManagerPlatformTimezoneGetResponse()
   * @return {!object} Deserialized ManagerPlatformTimezoneGetResponse value and its size
   */
  deserialize (value = new ManagerPlatformTimezoneGetResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ManagerPlatformTimezoneGetResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ManagerPlatformTimezoneGetResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ManagerPlatformTimezoneGetResponseModel}
   * @param {!number} prev Previous ManagerPlatformTimezoneGetResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ManagerPlatformTimezoneGetResponseModel };
